// Copyright 2024 - MinIO, Inc. All rights reserved.
import { createAsyncThunk } from "@reduxjs/toolkit";
import { obsApi } from "../../api";
import { ApiError, DataframeRow, XrayHealth } from "../../api/observeApi";
import {
  cleanDriveEndpointCommon,
  DriveProblem,
  extractDomainsCommonParts,
  SetCondition,
  setCondition,
} from "./dataMap.utils";
import { RootState } from "app/store";
import { CAInfoServer, ClusterAnalysis } from "./types";
import { processDiskList } from "./ErasureSet/ErasureSet.utils";
import { DateTime } from "luxon";
import { ExtendedDriveData } from "./ErasureSet/ErasureSetLayout.types";
import { EsetMetrics } from "./dataMap.types";

export const getHealthAsync = createAsyncThunk(
  "serverMap/getHealthAsync",
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState() as RootState;
    return obsApi.xRay
      .xRayHealth()
      .then((res) => {
        if (res.data) {
          const pCA = parseClusterAnalysis(res.data!, state.dataMap.sortBy);
          // now load live metrics
          dispatch(getLiveHealthAsync());
          return pCA;
        }
        return null;
      })
      .catch((res) => {
        console.error(res);
        return rejectWithValue(res.error as ApiError);
      });
  },
);
export const getLiveHealthAsync = createAsyncThunk(
  "serverMap/getLiveHealthAsync",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    return obsApi.xRay
      .xRayHealth({ live: true })
      .then((res) => {
        if (res.data) {
          const pCA = parseClusterAnalysis(res.data!, state.dataMap.sortBy);
          return pCA;
        }
        return null;
      })
      .catch((res) => {
        console.error(res);
        return rejectWithValue(res.error as ApiError);
      });
  },
);

export const getDriveDetailedMetrics = createAsyncThunk(
  "clusters/getDriveDetailedMetrics",
  async (drive: string, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const simplifiedDrive = cleanDriveEndpointCommon(
      drive,
      state.dataMap.endpointsCommonParts,
    );

    return obsApi.driveDetailedMetrics
      .driveDetailedMetrics({ startTime: 3600, drives: simplifiedDrive })
      .then((res) => {
        if (res.data.results) {
          const data = transformData(res.data.results);
          return data;
        }
        return null;
      })
      .catch((res) => {
        return rejectWithValue(res.error as ApiError);
      });
  },
);

export interface SerialData {
  name: string;
  data: number[];
}

export interface DriveDetailsData {
  series: SerialData[];
  timestamps: string[];
  features: string[];
}

const transformData = (metrics: DataframeRow[]): DriveDetailsData => {
  let timestamps: string[] = [];
  let features: string[] = [];
  let series: SerialData[] = [];

  if (!metrics || metrics.length === 0) {
    return { series, timestamps, features };
  }

  // Group metrics by feature
  const groupedMetrics: { [key: string]: DataframeRow[] } = {};
  metrics.forEach((metric) => {
    if (metric.feature! in groupedMetrics) {
      groupedMetrics[metric.feature!].push(metric);
    } else {
      groupedMetrics[metric.feature!] = [metric];
    }
    const date = new Date(metric.timestamp! * 1000);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    timestamps.push(formattedTime);
  });

  timestamps = [...new Set(timestamps)].sort();

  // Transform grouped metrics into series
  series = Object.keys(groupedMetrics).map((feature): SerialData => {
    // Sort metrics by timestamp
    const sortedMetrics = groupedMetrics[feature].sort(
      (a, b) => a.timestamp! - b.timestamp!,
    );

    // Extract feature_value, use 0 if undefined
    const data = sortedMetrics.map((metric) => metric.feature_value ?? 0);

    return {
      name: feature,
      data: data,
    };
  });

  features = Object.keys(groupedMetrics);
  return { series, timestamps, features };
};

export interface CAParse {
  drivesInformation: ExtendedDriveData[];
  analysisTimestamp: DateTime;
  analysis: ClusterAnalysis;
  poolServers: { [key: number]: CAInfoServer[] };
  setDrivesOffline: { [poolId: number]: { [setIndex: number]: number } };
  setDrivesCritical: { [poolId: number]: { [setIndex: number]: number } };
  setDrivesHealing: { [poolId: number]: { [setIndex: number]: number } };
  poolSetSize: { [poolId: number]: number };
  endpointsCommonParts: string[];
  zoom: "small" | "medium" | "large";
  drivesOffline: ExtendedDriveData[];
  drivesHealing: ExtendedDriveData[];
  totalCapacity: number;
  usedCapacity: number;
  esetMetrics: EsetMetrics;
}

// calculate all the xray metrics on the thunk to keep it outside the UI thread
export const parseClusterAnalysis = (
  actionPayload: XrayHealth,
  sortBy: "sets" | "server" | "capacity" | "status" | "used-space",
): CAParse => {
  let state: CAParse = {} as any as CAParse;
  const clustAnalysis = actionPayload!.health_info as ClusterAnalysis;
  state.analysis = clustAnalysis;
  state.analysisTimestamp = DateTime.fromSeconds(actionPayload!.taken_at!);
  if (actionPayload) {
    const drivesInformation = processDiskList(clustAnalysis, sortBy).drives;
    state.drivesInformation = drivesInformation;
    state.poolServers = {};

    let endpoints: string[] = [];

    let setDrivesOffline: {
      [poolId: number]: { [setIndex: number]: number };
    } = {};
    let setDrivesCritical: {
      [poolId: number]: { [setIndex: number]: number };
    } = {};
    let setDrivesHealing: {
      [poolId: number]: { [setIndex: number]: number };
    } = {};
    let poolSetSize: {
      [poolId: number]: number;
    } = {};

    let parity = 0;

    if (clustAnalysis && clustAnalysis.minio && clustAnalysis.minio.info) {
      if (clustAnalysis.minio.info.servers) {
        for (let i = clustAnalysis.minio.info.servers.length - 1; i >= 0; i--) {
          const server = clustAnalysis.minio.info.servers[i];
          endpoints.push(server.endpoint);
          if (server.drives) {
            if (
              !state.poolServers.hasOwnProperty(server.drives[0].pool_index)
            ) {
              state.poolServers[server.drives[0].pool_index] = [];
            }
            state.poolServers[server.drives[0].pool_index].push(server);

            server.drives?.forEach((d) => {
              if (!poolSetSize.hasOwnProperty(d.pool_index)) {
                poolSetSize[d.pool_index] = 0;
              }
              poolSetSize[d.pool_index] = Math.max(
                d.disk_index + 1,
                poolSetSize[d.pool_index],
              );

              if (d.state !== "ok" && d.state !== "warning") {
                if (!setDrivesOffline.hasOwnProperty(d.pool_index)) {
                  setDrivesOffline[d.pool_index] = {};
                }
                if (
                  !setDrivesOffline[d.pool_index].hasOwnProperty(d.set_index)
                ) {
                  setDrivesOffline[d.pool_index][d.set_index] = 0;
                }
                setDrivesOffline[d.pool_index][d.set_index]++;
              }

              if (
                d.metrics &&
                (d.metrics.totalErrorsTimeout > 0 ||
                  d.metrics.totalErrorsAvailability > 0)
              ) {
                if (!setDrivesCritical.hasOwnProperty(d.pool_index)) {
                  setDrivesCritical[d.pool_index] = {};
                }
                if (
                  !setDrivesCritical[d.pool_index].hasOwnProperty(d.set_index)
                ) {
                  setDrivesCritical[d.pool_index][d.set_index] = 0;
                }
                setDrivesCritical[d.pool_index][d.set_index]++;
              }
              if (d.healing) {
                if (!setDrivesHealing.hasOwnProperty(d.pool_index)) {
                  setDrivesHealing[d.pool_index] = {};
                }
                if (
                  !setDrivesHealing[d.pool_index].hasOwnProperty(d.set_index)
                ) {
                  setDrivesHealing[d.pool_index][d.set_index] = 0;
                }
                setDrivesHealing[d.pool_index][d.set_index]++;
              }
              // detect all the drive problems
              let problems: DriveProblem[] = [];
              const driveInfo = d;
              const driveOffline =
                driveInfo.state !== "ok" && driveInfo.state !== "warning";

              let usedPerc = (driveInfo.usedspace / driveInfo.totalspace) * 100;
              let usedInodes =
                (driveInfo.used_inodes /
                  (driveInfo.used_inodes + driveInfo.free_inodes)) *
                100;
              if (usedInodes >= 90) {
              }
              if (driveInfo.usedspace > 0) {
                usedPerc = Math.max(2, usedPerc);
              }

              // Drive is Offline
              if (driveOffline) {
                problems.push("offline");
              } else if (usedPerc >= 90 || usedInodes >= 90) {
                // Drive is almost full

                if (usedPerc >= 90) {
                  problems.push("90-perc-usage");
                }
                if (usedInodes >= 90) {
                  problems.push("90-perc-inodes");
                }
              } else if (usedPerc >= 80 || usedInodes >= 80) {
                // Drive is 75% capacity

                if (usedPerc >= 80) {
                  problems.push("80-perc-usage");
                }
                if (usedInodes >= 80) {
                  problems.push("80-perc-inodes");
                }
              } else if (
                usedPerc >= 70 ||
                usedInodes >= 70 ||
                driveInfo.state === "warning"
              ) {
                // Drive is 75% capacity
                if (usedPerc >= 70) {
                  problems.push("70-perc-usage");
                }
                if (usedInodes >= 70) {
                  problems.push("70-perc-inodes");
                }
              }
              if (driveInfo.healing) {
                problems.push("healing");
              }

              if (
                driveInfo.metrics &&
                (driveInfo.metrics.totalErrorsTimeout > 0 ||
                  driveInfo.metrics.totalErrorsAvailability > 0)
              ) {
                if (driveInfo.metrics.totalErrorsTimeout) {
                  problems.push("timeouts");
                } else if (driveInfo.metrics.totalErrorsAvailability) {
                  problems.push("io-errors");
                }
              }
              d.problems = problems;
            });
          }
        }
      }

      if (clustAnalysis.minio.info.backend) {
        parity = clustAnalysis.minio.info.backend.standardSCParity;
      }
    }

    state.setDrivesOffline = setDrivesOffline;
    state.setDrivesCritical = setDrivesCritical;
    state.setDrivesHealing = setDrivesHealing;
    state.poolSetSize = poolSetSize;

    const setConditionMap: {
      [poolId: number]: { [setIndex: number]: SetCondition };
    } = {};
    const setConditionCount: Record<SetCondition, number> = {
      normal: 0,
      warning: 0,
      severe: 0,
      critical: 0,
      unusable: 0,
    };
    if (
      clustAnalysis &&
      clustAnalysis.minio &&
      clustAnalysis.minio.info &&
      clustAnalysis.minio.info.servers
    ) {
      clustAnalysis.minio.info.servers.forEach((srv) =>
        srv.drives?.forEach((drv) => {
          let drivesOffline = 0;
          if (
            setDrivesOffline.hasOwnProperty(drv.pool_index) &&
            setDrivesOffline[drv.pool_index].hasOwnProperty(drv.set_index)
          ) {
            drivesOffline = setDrivesOffline[drv.pool_index][drv.set_index];
          }

          drv.set_condition = setCondition(parity, drivesOffline);

          if (!setConditionMap.hasOwnProperty(drv.pool_index)) {
            setConditionMap[drv.pool_index] = {};
          }
          if (!setConditionMap[drv.pool_index].hasOwnProperty(drv.set_index)) {
            setConditionMap[drv.pool_index][drv.set_index] = drv.set_condition;
            setConditionCount[drv.set_condition]++;
          }
        }),
      );
    }

    state.esetMetrics = setConditionCount as EsetMetrics;

    for (let key in state.poolServers) {
      state.poolServers[key].sort((a, b) =>
        a.endpoint.localeCompare(b.endpoint, undefined, {
          numeric: true,
        }),
      );
    }

    state.endpointsCommonParts = extractDomainsCommonParts(endpoints);

    let newSize = "medium";
    if (drivesInformation.length > 500) {
      newSize = "small";
    } else if (drivesInformation.length < 50) {
      newSize = "large";
    }
    state.zoom = newSize as "small" | "medium" | "large";
    state.drivesOffline = drivesInformation.filter(
      (d) => d.driveInfo.state !== "ok" && d.driveInfo.state !== "warning",
    );
    state.drivesHealing = drivesInformation.filter((d) => d.driveInfo.healing);
    if (drivesInformation.length === 0) {
      state.totalCapacity = 0;
    } else {
      state.totalCapacity = drivesInformation
        .map((d) => d.driveInfo.totalspace ?? 0)
        .reduce((a, b) => a + b);
      state.usedCapacity = drivesInformation
        .map((d) => d.driveInfo.usedspace ?? 0)
        .reduce((a, b) => a + b);
    }
  }
  return state;
};
